import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import "./WebDevelopment.css";
import webDevelopmentImage from "../../Assets/Services/webDevelopment_service_systemImg.png";
import webDevelopmentHeroImage from "../../Assets/Services/webDevelopment_hero_img.jpeg";
import { Helmet } from "react-helmet";
import Nav from "../../Home/navbar/nav";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import axios from "axios";
import { countries } from "../../constants/countries";
import { AiOutlineCheckCircle } from "react-icons/ai";
import { isPossiblePhoneNumber } from "libphonenumber-js";

const WebDevelopment = () => {
  const impactSectionRef = useRef(null);
  const contactSectionRef = useRef(null);
  const heroSectionRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isChanging, setIsChanging] = useState(false);
  const [currentMaxDigits, setCurrentMaxDigits] = useState(10);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const phoneInputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    organization: "",
    region: "",
    message: "",
    countryCode: "",
    nationalNumber: "",
    fullNumber: "",
  });

  const processSteps = [
    {
      title: "Requirement Gathering",
      color: "#ae00ff",
      glow: "rgba(174, 0, 255, 0.45)",
      items: [
        "Understand the client's goals and target audience.",
        "Define the website's purpose and features.",
        "Collect functional and technical requirements.",
      ],
    },
    {
      title: "Planning",
      color: "#5b00ff",
      glow: "rgba(91, 0, 255, 0.45)",
      items: [
        "Create a project roadmap and timeline.",
        "Decide on the technology stack.",
        "Design the site architecture and navigation.",
      ],
    },
    {
      title: "UI/UX Design",
      color: "#00d9e6",
      glow: "rgba(0, 217, 230, 0.45)",
      items: [
        "Create wireframes and mockups.",
        "Design the visual appearance.",
        "Focus on user experience (UX) and responsive design.",
      ],
    },
    {
      title: "Frontend Development",
      color: "#20d000",
      glow: "rgba(32, 208, 0, 0.45)",
      items: [
        "Convert design into responsive frontend code.",
        "Add interactive features and website functionality.",
        "Test responsiveness and optimize website performance.",
      ],
    },
    {
      title: "Backend Development",
      color: "#d4d400",
      glow: "rgba(212, 212, 0, 0.45)",
      items: [
        "Build secure server and database infrastructure.",
        "Develop APIs and implement business logic.",
        "Test security, performance, and backend functionality.",
      ],
    },
    {
      title: "Testing & Quality Assurance",
      color: "#ff1010",
      glow: "rgba(255, 16, 16, 0.45)",
      items: [
        "Test functionality and performance.",
        "Identify and fix bugs.",
        "Perform security and usability testing.",
      ],
    },
    {
      title: "Deployment & Launch",
      color: "#ff7800",
      glow: "rgba(255, 120, 0, 0.45)",
      items: [
        "Upload the website to a web server.",
        "Configure the domain name and hosting.",
        "Set up databases and server environments.",
      ],
    },
    {
      title: "Maintenance & Support",
      color: "#ff0066",
      glow: "rgba(255, 0, 102, 0.45)",
      items: [
        "Monitor website performance.",
        "Fix bugs and security vulnerabilities.",
        "Add new features as requirements evolve.",
      ],
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIsChanging(true);

      setTimeout(() => {
        setCurrentStep((prev) => (prev + 1) % processSteps.length);
        setIsChanging(false);
      }, 400);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  const step = processSteps[currentStep];

  const scrollToContact = () => {
    if (!contactSectionRef.current) return;

    const targetY =
      contactSectionRef.current.getBoundingClientRect().top + window.scrollY;

    const scrollProxy = { y: window.scrollY };

    gsap.to(scrollProxy, {
      y: targetY,
      duration: 1,
      ease: "power2.inOut",
      onUpdate: () => window.scrollTo(0, scrollProxy.y),
    });
  };

  const filteredCountries = countries.filter((country) => {
    const search = countrySearch.toLowerCase().trim();

    return (
      country.name.toLowerCase().includes(search) ||
      country.code.toLowerCase().includes(search)
    );
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (loading) return;

    const apiURL = process.env.REACT_APP_CONTACTUS_HOST;
    const hasPhoneInput =
      formData.phone && formData.phone.trim() !== "";

    if (
      !formData.fullName ||
      !formData.organization ||
      !formData.message ||
      !formData.email ||
      !formData.region ||
      !hasPhoneInput
    ) {
      return;
    }

  const phoneIsValid = isPossiblePhoneNumber(formData.phone);

    if (!phoneIsValid) {
      setPhoneError("Please enter a valid phone number.");
      return;
    }

    setPhoneError("");

    setLoading(true);

    const payload = {
      fullName: formData.fullName,
      email: formData.email,
      organization: formData.organization,
      region: formData.region,
      phone: formData.phone,
      message: formData.message,
    };

    const handleSuccessUI = () => {
      setShowSuccessMessage(true);

      setFormData({
        fullName: "",
        email: "",
        organization: "",
        region: "",
        phone: "",
        message: "",
      });

      setPhoneError("");

      setCurrentMaxDigits(10);

      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 3000);
    };

    axios
      .post(apiURL, payload)
      .then((response) => {
        handleSuccessUI();
      })
      .catch((error) => {
        if (error.message === "Network Error" || !error.response) {
          handleSuccessUI();
        } else {
          alert(
            "Server error. Please check your connection and try again."
          );

          console.error("True API Error:", error);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useLayoutEffect(() => {
    const section = heroSectionRef.current;

    if (!section) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline();

      tl.fromTo(
        ".web-hero-title",
        {
          opacity: 0,
          y: 10,
        },
        {
          opacity: 1,
          y: 0,
          duration: 1.5,
          ease: "power2.out",
        }
      ).fromTo(
        ".web-hero-subtitle",
        {
          opacity: 0,
          y: 8,
        },
        {
          opacity: 1,
          y: 0,
          duration: 1.5,
          ease: "power2.out",
        },
        "-=1"
      );
    }, section);

    return () => ctx.revert();
  }, []);

  useLayoutEffect(() => {
    const section = impactSectionRef.current;

    if (!section) return;

    const ctx = gsap.context(() => {
      gsap.set(section, {
        backgroundColor: "#ffffff",
      });

      gsap.set(".web-development-impact-content", {
        opacity: 1,
        scale: 1,
        color: "#000000",
      });

      gsap.set(".impact-final-content", {
        opacity: 0,
        scale: 1,
      });

      const mm = gsap.matchMedia();

      mm.add(
        {
          desktop: "(min-width: 769px)",
          mobile: "(max-width: 768px)",
        },
        (context) => {
          const { desktop } = context.conditions;

          const circleStart = desktop ? "58vh" : "42vh";
          const finalCircleY = desktop ? "40px" : "20px";

          gsap.set(".impact-circle-top", {
            opacity: 0,
            xPercent: -50,
            yPercent: -50,
            y: `-${circleStart}`,
            scale: 1,
          });

          gsap.set(".impact-circle-bottom", {
            opacity: 0,
            xPercent: -50,
            yPercent: -50,
            y: circleStart,
            scale: 1,
          });

          const tl = gsap.timeline({
            scrollTrigger: {
              trigger: section,
              start: "top top",
              end: desktop ? "+=3000" : "+=2400",
              pin: true,
              scrub: 1.2, // Increased slightly for ultra-smooth trackpad scrubbing
              anticipatePin: 1,
              invalidateOnRefresh: true,
            },
          });

          // ------------------------------------------------
          // STEP 1: Background White -> Black
          // ------------------------------------------------
          tl.to(section, {
            backgroundColor: "#000000",
            duration: 1,
            ease: "power1.inOut",
          }).to(
            ".web-development-impact-content",
            {
              color: "#ffffff",
              duration: 1,
              ease: "power1.inOut",
            },
            "<",
          );

          tl.to({}, { duration: 0.4 });

          tl.to(".web-development-impact-content", {
            opacity: 0,
            scale: 0.96,
            duration: 1,
            ease: "power2.inOut",
          });

          tl.to(
            [".impact-circle-top", ".impact-circle-bottom"],
            {
              opacity: 1,
              duration: 1,
              ease: "power1.out",
            },
            "<",
          );

          tl.to({}, { duration: 0.4 });

          tl.to(".impact-circle-top", {
            y: desktop ? "calc(-20vh + 40px)" : "calc(-14vh + 20px)",
            duration: 1.5,
            ease: "sine.inOut",
          }).to(
            ".impact-circle-bottom",
            {
              y: desktop ? "calc(20vh + 40px)" : "calc(14vh + 20px)",
              duration: 1.5,
              ease: "sine.inOut",
            },
            "<",
          );

          tl.to({}, { duration: 0.3 });

          tl.to(".impact-circle-top", {
            y: finalCircleY,
            duration: 2,
            ease: "sine.inOut",
          }).to(
            ".impact-circle-bottom",
            {
              y: finalCircleY,
              duration: 2,
              ease: "sine.inOut",
            },
            "<",
          );

          tl.to(
            ".impact-final-content",
            {
              opacity: 1,
              duration: 1.8,
              ease: "power2.out",
            },
            "-=1.2",
          );

          tl.to({}, { duration: 0.8 });

          return () => {
            tl.kill();
          };
        },
      );
    }, section);

    const refreshOnLoad = () => ScrollTrigger.refresh();

    if (document.readyState === "complete") {
      refreshOnLoad();
    } else {
      window.addEventListener("load", refreshOnLoad);
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(refreshOnLoad);
    }

    return () => {
      window.removeEventListener("load", refreshOnLoad);
      ctx.revert();
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    const textOnlyFields = [
      "fullName",
      "organization",
      "region",
      "message",
    ];

    if (textOnlyFields.includes(name)) {
      const onlyText = value.replace(/[^a-zA-Z\s]/g, "");

      setFormData({
        ...formData,
        [name]: onlyText,
      });
    } else if (name === "phone") {
      const numericAndPlus = value.replace(/[^0-9+\s]/g, "");

      setFormData({
        ...formData,
        [name]: numericAndPlus,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handlePhoneChange = (e) => {
    const rawValue = e.target.value;

    const cleanedValue = rawValue.replace(/[^0-9+\s]/g, "");

    if (!cleanedValue.startsWith("+")) {
      handleChange({
        target: {
          name: "phone",
          value: cleanedValue,
        },
      });

      setPhoneError("");
      return;
    }

    const matched = countries
      .filter((country) => cleanedValue.startsWith(country.code))
      .sort((a, b) => b.code.length - a.code.length)[0];

    if (!matched) {
      handleChange({
        target: {
          name: "phone",
          value: cleanedValue,
        },
      });

      setPhoneError("");
      return;
    }

    const phoneDigits = cleanedValue
      .slice(matched.code.length)
      .replace(/\D/g, "");

    const formattedValue = `${matched.code} ${phoneDigits}`;

    handleChange({
      target: {
        name: "phone",
        value: formattedValue,
      },
    });

    if (phoneDigits.length === 0) {
      setPhoneError("");
      return;
    }

    const isValid = isPossiblePhoneNumber(formattedValue);

    if (!isValid) {
      setPhoneError("Please enter a valid phone number.");
    } else {
      setPhoneError("");
    }
  };

  useEffect(() => {
    const handleOutsideClick = () => setIsOpen(false);
    window.addEventListener("click", handleOutsideClick);
    return () => window.removeEventListener("click", handleOutsideClick);
  }, []);

  return (
    <>
      <Helmet>
        <title>Website & Web Application Development | Althi Solutions</title>
        <link
          rel="preload"
          as="image"
          href={webDevelopmentHeroImage}
          fetchPriority="high"
        />
        <meta
          name="description"
          content="Create responsive websites and web applications with Althi Solutions, designed for better performance, seamless experiences, scalability, and business growth."
        />
        <link
          rel="canonical"
          href="https://www.althisolutions.com/services/web-development"
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />

        <meta
          property="og:title"
          content="Website & Web Application Development | Althi Solutions"
        />

        <meta
          property="og:description"
          content="Create responsive websites and web applications with Althi Solutions, designed for better performance, seamless experiences, scalability, and business growth."
        />

        <meta
          property="og:url"
          content="https://www.althisolutions.com/services/web-development"
        />

        <meta
          property="og:image"
          content="https://www.althisolutions.com/thumbnail.jpg"
        />

        <meta property="og:site_name" content="Althi Solutions" />

        <meta name="twitter:card" content="summary_large_image" />

        <meta name="twitter:site" content="@AlthiSolutions" />

        <meta name="twitter:creator" content="@AlthiSolutions" />

        <meta
          name="twitter:title"
          content="Website & Web Application Development | Althi Solutions"
        />

        <meta
          name="twitter:description"
          content="Create responsive websites and web applications with Althi Solutions, designed for better performance, seamless experiences, scalability, and business growth."
        />

        <meta
          name="twitter:image"
          content="https://www.althisolutions.com/thumbnail.jpg"
        />   
      </Helmet>

      <Nav />

      <div className="web-development-page">
      <section ref={heroSectionRef} className="web-development-hero">
        <img
          src={webDevelopmentHeroImage}
          alt="Web Development"
          className="web-development-hero-image"
          fetchPriority="high"
          loading="eager"
        />

        <div className="web-development-hero-overlay">
          <div className="web-development-hero-content">
            <h1 className="web-hero-title">Web Development</h1>
            <p className="web-hero-subtitle">
              Build Smarter Websites for a Digital Future
            </p>
          </div>
        </div>
      </section>

        <section className="web-development-content">
          <div className="web-development-image-wrapper">
            <img
              src={webDevelopmentImage}
              alt="Web Development"
              className="web-development-image"
            />
          </div>

          <div className="web-development-description">
            <p>
              Bring your vision to life with websites built around your unique
              business needs. We develop modern, responsive, and high-performing
              websites that combine seamless functionality, engaging
              experiences, and scalable technology to strengthen your digital
              presence.
            </p>
          </div>
        </section>

        <section className="third-section-container">
          <div className="third-section">
            <h2>Althi Process</h2>
            <p>
              The web development process is a structured approach to designing,
              building, testing, and maintaining.
            </p>

            <div
              className={`requirement-container ${
                isChanging ? "process-changing" : ""
              }`}
              style={{
                "--process-color": step.color,
                "--process-glow": step.glow,
              }}
            >
              <div className="process-content">
                <h5>{step.title}</h5>

                <ul>
                  {step.items.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
        <section ref={impactSectionRef} className="web-development-impact">
          <div className="impact-animation-bg"></div>

          <div className="web-development-impact-content">
            <h2 className="impact-heading">Our Impact</h2>

            <div className="impact-para">
              <p>
                We help businesses streamline their operations, improve
                productivity, and achieve better results through reliable and
                innovative technology solutions. Our approach focuses on
                understanding real business challenges and delivering practical
                solutions that create lasting value.
              </p>
            </div>

            <div className="impact-percent-section">
              <div className="impact-percent-one">
                <span>30+</span>

                <p>Projects Successfully Delivered</p>
              </div>

              <div className="impact-percent-two">
                <span>90%</span>

                <p>Businesses Supported</p>
              </div>

              <div className="impact-percent-three">
                <span>100%</span>

                <p>Focus on Customer Satisfaction</p>
              </div>
            </div>
          </div>
          <div className="impact-circle impact-circle-top"></div>

          <div className="impact-circle impact-circle-bottom"></div>

          <div className="impact-final-content">
            <h2 className="content-header">
              <span className="line-break">Ongoing Support</span>
              <br />
              <span className="line-break">&amp; Maintenance</span>
            </h2>

            <p>
              Our dedicated team offers continuous support, timely updates, and
              proactive maintenance to keep your website or software performing
              at its best.
            </p>

            <button onClick={scrollToContact}>
              Let's talk <span>→</span>
            </button>
          </div>
        </section>

        <section ref={contactSectionRef} className="web-development-contact">
          <div className="lets-talk-section">
            <div className="lets-talk-header">
              <h2>Let's Talk</h2>
              <p>(Request For Service)</p>
            </div>

            <form className="lets-talk-form" onSubmit={handleSubmit}>
              <div>
                <label>Full Name</label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <label>Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div
                className="lets-talk-field-group"
                onClick={(e) => e.stopPropagation()}
              >
                <label htmlFor="phone">Phone Number</label>

                <div className="input-with-dropdown-wrapper">
                  <input
                    type="text"
                    id="phone"
                    name="phone"
                    ref={phoneInputRef}
                    className="contact-form-input-talk"
                    value={formData.phone || ""}
                    onClick={() => setIsOpen(true)}
                    onChange={handlePhoneChange}
                    placeholder="+91 xxxxxxxxxx"
                    required
                  />
                  {phoneError && (
                    <small className="phone-error-message">
                      {phoneError}
                    </small>
                  )}

                  <span className={`dropdown-arrow ${isOpen ? "open" : ""}`}>
                    ▾
                  </span>

                  {isOpen && (
                    <div
                      className="country-dropdown-talk custom-scrollbar-talk"
                      data-lenis-prevent
                    >
                      <div className="country-search-wrapper-talk">
                        <input
                          type="text"
                          className="country-search-input-talk"
                          placeholder="Search country or code..."
                          value={countrySearch}
                          onChange={(e) => setCountrySearch(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                        />
                      </div>

                      <ul className="country-dropdown-list-talk">
                        {filteredCountries.length > 0 ? (
                          filteredCountries.map((country, index) => (
                            <li
                              key={index}
                              className="country-dropdown-item"
                              onClick={() => {
                                handleChange({
                                  target: {
                                    name: "phone",
                                    value: `${country.code} `,
                                  },
                                });

                                setIsOpen(false);
                                setCountrySearch("");

                                setTimeout(() => {
                                  if (phoneInputRef.current) {
                                    phoneInputRef.current.focus();
                                  }
                                }, 0);
                              }}
                            >
                              <span className="country-name">
                                {country.name}
                              </span>

                              <span className="country-code">
                                {country.code}
                              </span>
                            </li>
                          ))
                        ) : (
                          <li className="no-country-found-talk">
                            No country found
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label>Organization</label>
                <input
                  type="text"
                  id="organization"
                  name="organization"
                  value={formData.organization}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <label>Region</label>
                <input
                  type="text"
                  id="region"
                  name="region"
                  value={formData.region}
                  onChange={handleChange}
                  required
                />
              </div>

              <div>
                <label>How Can We Help You.....</label>
                <input
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="submit-btn-container">
                <button
                  type="submit"
                  disabled={loading}
                  className={loading ? "submit-btn loading" : "submit-btn"}
                >
                  {loading ? "Submitting..." : "Submit"}
                  <span className="submit-arrow">→</span>
                </button>
              </div>
            </form>

            {showSuccessMessage && (
              <div
                className="success-messages"
                style={{ marginBottom: "15px" }}
              >
                <div
                  className="success-message-contents"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "#4BB543",
                  }}
                >
                  <div className="success-message-headings">
                    Submitted Successfully!
                  </div>
                  <AiOutlineCheckCircle className="check-icon" />
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default WebDevelopment;
